import { useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ZodBooleanDef } from "zod";
import AddIcon from "../../../../../assets/images/icons/add.svg";
import Remove from "../../../../../assets/images/x-02.svg";
import { gridColsMap } from "../../../../../Constants/SocialLinks";
import {
  MedicalFields,
  MedicalKeys,
} from "../../../../../enums/MedicalReportEnums";
import { IStepFields } from "../../../../../interface/MedicalRecord";
import { FieldRenderer } from "../../FieldRenderer/FieldRenderer";
import GeneralSelectApi, {
  GeneralSelectApiProps,
} from "../../../../../components/Inputs/GeneralSelectApi";
import { useAppMediaQuery } from "../../../../../hooks";

interface GeneralSelectWithInputProps extends GeneralSelectApiProps {
  fieldsConfig: IStepFields;
  disabled?: ZodBooleanDef;
  hasExternalProp?: boolean;
  isRequired?: boolean;
  fieldArray?: any;
}

const GeneralSelectWithInput = ({
  name,
  fieldsConfig,
  hasExternalProp = true,
  isRequired,
  onChange,
  fieldArray,
  ...props
}: GeneralSelectWithInputProps) => {
  const { control, setValue, watch, formState } = useFormContext();
  const { t } = useTranslation();
  const { isMobileOrTablet } = useAppMediaQuery();

  const [inputValue, setInputValue] = useState("");

  const optionalFieldArray = useFieldArray({
    control,
    name,
  });

  const {
    fields: values,
    append,
    remove,
    replace,
  } = fieldArray ?? optionalFieldArray;

  const selectedOption = watch(`${name}-option`) ?? [];

  const showValueRow = fieldsConfig?.InverseParentStepFields?.length > 0;
  const isFamilyHistory = name === MedicalKeys?.FamilyHistory;

  const fields = fieldsConfig?.InverseParentStepFields ?? [];
  const allExceptLast = fields.slice(0, -1);
  const last = fields.at(-1);

  const optionChildren = isFamilyHistory ? allExceptLast : fields;
  const optionExtraChildren = isFamilyHistory && last ? [last] : [];

  const itemsLength =
    fieldsConfig?.InverseParentStepFields?.length + (isFamilyHistory ? 0 : 1);

  const gridColsClass = isMobileOrTablet
    ? "grid-cols-1"
    : gridColsMap[itemsLength as keyof typeof gridColsMap] || "grid-cols-1";

  const hasExternal = hasExternalProp && !isFamilyHistory;

  return (
    <div>
      <GeneralSelectApi
        name={`${name}-option`}
        isMulti
        value={selectedOption}
        inputValue={inputValue}
        onInputChange={setInputValue}
        onChange={(options) => {
          const opts = options ?? [];

          // keys from select
          const nextKeys = opts.map((opt: any) =>
            opt.IsExternal ? `external-${opt.label}` : opt.value,
          );

          // keys from fieldArray
          const currentKeys = values.map((v: any) =>
            v?.Key || v?.IsExternal
              ? `external-${v?.label || v.Name}`
              : v?.value || v.Id,
          );

          currentKeys.forEach((key: any, index: any) => {
            if (!nextKeys.includes(key)) {
              remove(index);
            }
          });

          opts.forEach((opt: any) => {
            const key = opt.IsExternal ? `external-${opt.label}` : opt.value;

            if (!currentKeys.includes(key)) {
              append(
                opt.IsExternal
                  ? {
                      Key: name,
                      Name: opt.label,
                      IsExternal: true,
                      ...opt,
                    }
                  : {
                      Id: opt.value,
                      Name: opt.label,
                      ...opt,
                    },
              );
            }
          });
          // replace(opts)

          setValue(`${name}-option`, opts, { shouldDirty: true });

          onChange && onChange(opts);
        }}
        noOptionsMessage={
          hasExternal
            ? ({ inputValue }) => (
                <div
                  className="flex cursor-pointer gap-3 bg-[#F5F7F9] px-3 py-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    append({
                      Key: name,
                      Name: inputValue,
                      IsExternal: true,
                    });

                    setValue(`${name}-option`, [
                      ...selectedOption,
                      {
                        label: inputValue,
                        value: inputValue,
                        IsExternal: true,
                      },
                    ]);

                    setInputValue("");

                    setTimeout(() => {
                      const selectElement = document.activeElement;
                      if (selectElement instanceof HTMLElement) {
                        selectElement.blur();
                      }
                    }, 50);
                  }}
                >
                  <img src={AddIcon} alt="add" />
                  <p className="text-primary-text">
                    {t("add")} "{inputValue}" {t("as_new")}
                  </p>
                </div>
              )
            : undefined
        }
        customControlStyles={{
          background: "white !important",
          minHeight: "50px !important",
          border: "1px solid #566B8D !important",
          borderRadius: "8px",
        }}
        {...props}
      />

      {!!isRequired && (
        <div className="mt-1 text-sm text-error">{t("required")}</div>
      )}

      {!!itemsLength &&
        showValueRow &&
        values.map((item: any, index: number) => {
          return (
            <div
              key={item.id}
              className={`grid ${gridColsClass} mt-3 gap-1 px-3 py-2`}
            >
              <div className="flex items-center gap-4">
                <img
                  alt="remove"
                  src={Remove}
                  className="h-5 w-5 cursor-pointer"
                  onClick={() => {
                    remove(index);
                    setValue(
                      `${name}-option`,
                      selectedOption?.filter((_: any, i: number) => {
                        return i !== index;
                      }),
                    );
                  }}
                />
                <p className="flex-1 truncate">{item.Name}</p>
              </div>

              {optionChildren?.map((child, i) => {
                return child?.Type !== MedicalFields.Option ? (
                  <FieldRenderer
                    key={i}
                    field={{
                      ...child,
                      Key: `${name}.${index}.${child.Key ?? child.Id}`,
                      ParentName: `${name}.${index}`,
                      IsRequired: (formState?.errors as any)?.[name ?? ""]?.[
                        `${index ?? 0}`
                      ]?.[child.Key ?? child.Id]?.message,
                    }}
                  />
                ) : (
                  <></>
                );
              })}
            </div>
          );
        })}

      {optionExtraChildren?.length > 0 &&
        values.map((item: any, index: number) => (
          <div
            key={`${item.id}-extra`}
            className={`grid ${gridColsClass} px-3 py-2`}
          >
            <p className="truncate">{item.Name}</p>
            {optionExtraChildren.map((child, i) => {
              return (
                child.Type !== MedicalFields.Option && (
                  <FieldRenderer
                    key={i}
                    field={{
                      ...child,
                      Key: `${name}.${index}.${child.Key ?? child.Id}`,
                      ParentName: `${name}.${index}`,
                      IsRequired: (formState?.errors as any)?.[name ?? ""]?.[
                        `${index ?? 0}`
                      ]?.[child.Key ?? child.Id]?.message,
                    }}
                  />
                )
              );
            })}
          </div>
        ))}
    </div>
  );
};

export default GeneralSelectWithInput;
